/**
 * Constants - Application-wide constants, enums, and configuration values
 * 
 * Features:
 * - API endpoints and timeout values
 * - User roles and permission constants
 * - Market type definitions
 * - Error message constants
 * - Configuration for refresh intervals
 * - Validation rules and limits
 */

/**
 * API Endpoints
 */
export const API_ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    LOGOUT: '/api/auth/logout',
    REFRESH_TOKEN: '/api/auth/refresh',
    FORGOT_PASSWORD: '/api/auth/forgot-password',
    RESET_PASSWORD: '/api/auth/reset-password',
    VERIFY_EMAIL: '/api/auth/verify-email'
  },

  // Subscription management endpoints
  SUBSCRIPTION: {
    STATUS: '/api/subscriptions/status',
    ACTIVATE: '/api/subscriptions/activate',
    DEACTIVATE: '/api/subscriptions/deactivate',
    HISTORY: '/api/subscriptions/history',
    PLANS: '/api/subscriptions/plans'
  },

  // Core surebet functionality
  SUREBET: {
    MARKETS_ANALYSIS: '/api/core/markets-analysis',
    BOOKMAKERS: '/api/core/bookmakers',
    SPORTS: '/api/core/sports',
    CHAMPIONSHIPS: '/api/core/championships'
  },

  // User management
  USER: {
    PROFILE: '/api/users/profile',
    UPDATE_PROFILE: '/api/users/profile',
    PREFERENCES: '/api/users/preferences',
    DELETE_ACCOUNT: '/api/users/delete'
  },

  // Admin endpoints
  ADMIN: {
    USERS: '/api/admin/users',
    SUBSCRIPTIONS: '/api/admin/subscriptions',
    ANALYTICS: '/api/admin/analytics',
    SYSTEM_STATUS: '/api/admin/system-status'
  }
};

/**
 * API Timeout Values (in milliseconds)
 */
export const API_TIMEOUTS = {
  DEFAULT: 10000,           // 10 seconds - Default request timeout
  AUTHENTICATION: 15000,     // 15 seconds - Authentication requests
  FILE_UPLOAD: 30000,       // 30 seconds - File upload requests
  LONG_POLLING: 60000,       // 60 seconds - Long polling requests
  SUBSCRIPTION: 20000,      // 20 seconds - Subscription requests
  DATA_REFRESH: 15000,       // 15 seconds - Data refresh requests
  RETRY_DELAY: 2000         // 2 seconds - Retry delay between attempts
};

/**
 * HTTP Status Codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  PAYMENT_REQUIRED: 402,    // Subscription required
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503
};

/**
 * User Roles
 */
export const USER_ROLES = {
  USER: 'User',
  ADMIN: 'Admin',
  SUPER_ADMIN: 'Super Admin'
};

/**
 * Permissions
 */
export const PERMISSIONS = {
  VIEW_SUREBETS: 'view_surebets',
  USE_CALCULATOR: 'use_calculator',
  MANAGE_SUBSCRIPTION: 'manage_subscription',
  ADMIN_USERS: 'admin_users',
  ADMIN_SUBSCRIPTIONS: 'admin_subscriptions',
  SYSTEM_ADMIN: 'system_admin'
};

/**
 * Role to Permission Mapping
 */
export const ROLE_PERMISSIONS = {
  [USER_ROLES.USER]: [
    PERMISSIONS.VIEW_SUREBETS,
    PERMISSIONS.USE_CALCULATOR,
    PERMISSIONS.MANAGE_SUBSCRIPTION
  ],
  [USER_ROLES.ADMIN]: [
    PERMISSIONS.VIEW_SUREBETS,
    PERMISSIONS.USE_CALCULATOR,
    PERMISSIONS.MANAGE_SUBSCRIPTION,
    PERMISSIONS.ADMIN_USERS,
    PERMISSIONS.ADMIN_SUBSCRIPTIONS
  ],
  [USER_ROLES.SUPER_ADMIN]: [
    PERMISSIONS.VIEW_SUREBETS,
    PERMISSIONS.USE_CALCULATOR,
    PERMISSIONS.MANAGE_SUBSCRIPTION,
    PERMISSIONS.ADMIN_USERS,
    PERMISSIONS.ADMIN_SUBSCRIPTIONS,
    PERMISSIONS.SYSTEM_ADMIN
  ]
};

/**
 * Subscription Status
 */
export const SUBSCRIPTION_STATUS = {
  INACTIVE: 'inactive',
  ACTIVE: 'active',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  PENDING: 'pending'
};

/**
 * Market Types (Margin Types)
 */
export const MARKET_TYPES = {
  MATCH_RESULT: 'margin1X2',
  OVER_UNDER_2_5: 'marginOverUnder2AndHalf',
  BOTH_SCORE: 'marginBothScore',
  ONE_X_VS_2: 'margin1Xvs2',
  X2_VS_1: 'marginX2vs1',
  TWELVE_VS_X: 'margin12vsX',
  OVER_UNDER_1_5: 'marginOverUnder1AndHalf',
  OVER_UNDER_3_5: 'marginOverUnder3AndHalf'
};

/**
 * Coefficient Types
 */
export const COEFFICIENT_TYPES = {
  // 1X2 Market
  HOME_WIN: 'Coef1',
  DRAW: 'CoefX',
  AWAY_WIN: 'Coef2',

  // Over/Under Markets
  OVER_1_5: 'CoefGoalOver1AndHalf',
  UNDER_1_5: 'CoefGoalUnder1AndHalf',
  OVER_2_5: 'CoefGoalOver2AndHalf',
  UNDER_2_5: 'CoefGoalUnder2AndHalf',
  OVER_3_5: 'CoefGoalOver3AndHalf',
  UNDER_3_5: 'CoefGoalUnder3AndHalf',

  // Both Teams to Score
  BOTH_SCORE_YES: 'CoefBothScoreYes',
  BOTH_SCORE_NO: 'CoefBothScoreNo',

  // Double Chance
  HOME_OR_DRAW: 'Coef1X',
  DRAW_OR_AWAY: 'CoefX2',
  HOME_OR_AWAY: 'Coef12',

  // Goal/No Goal
  YES_GOAL: 'CoefYesGoal',
  NO_GOAL: 'CoefNoGoal'
};

/**
 * Bookmaker Identifiers
 */
export const BOOKMAKERS = {
  BETANO: 'betano',
  BET365: 'bet365',
  WINBET: 'winbet',
  BETWILD: 'betwild',
  SLYBET: 'slybet',
  BWIN: 'bwin',
  EFBET: 'efbet',
  INBET: 'inbet',
  ALPHAWIN: 'alphawin',
  SESAME: 'sesame',
  ELITBET: 'elitbet',
  BETHUB: 'bethub',
  BET_BG: 'bet',
  TOPWIN: 'topwin',
  MRBIT: 'mrbit',
  ADMIRALBET: 'admiralbet',
  BG8888: 'bg8888',
  PALMSBET: 'palmsbet',
  EVERBET: 'everbet',
  MAGICBET: 'magicbet',
  BETVAM: 'betvam',
  QBET: 'qbet',
  IMPERIABET: 'imperiabet'
};

/**
 * Sports Types
 */
export const SPORTS = {
  FOOTBALL: 'Football',
  BASKETBALL: 'Basketball',
  TENNIS: 'Tennis',
  VOLLEYBALL: 'Volleyball',
  HANDBALL: 'Handball',
  ICE_HOCKEY: 'Ice Hockey'
};

/**
 * Languages
 */
export const LANGUAGES = {
  BULGARIAN: 'bg',
  ENGLISH: 'en'
};

/**
 * Error Message Constants
 */
export const ERROR_MESSAGES = {
  // Network errors
  NETWORK_ERROR: {
    bg: 'Грешка в мрежата. Моля, проверете връзката си',
    en: 'Network error. Please check your connection'
  },
  TIMEOUT_ERROR: {
    bg: 'Заявката отне прекалено много време',
    en: 'Request took too long'
  },
  CONNECTION_FAILED: {
    bg: 'Неуспешно свързване със сървъра',
    en: 'Failed to connect to server'
  },

  // Authentication errors
  AUTH_REQUIRED: {
    bg: 'Необходима е автентикация за достъп',
    en: 'Authentication required for access'
  },
  AUTH_FAILED: {
    bg: 'Неуспешна автентикация',
    en: 'Authentication failed'
  },
  TOKEN_EXPIRED: {
    bg: 'Сесията е изтекла. Моля, влезте отново',
    en: 'Session expired. Please login again'
  },
  INVALID_CREDENTIALS: {
    bg: 'Невалидни потребителски данни',
    en: 'Invalid credentials'
  },

  // Subscription errors
  SUBSCRIPTION_REQUIRED: {
    bg: 'Необходим е активен абонамент за достъп до арбитражните възможности',
    en: 'Active subscription required to access arbitrage opportunities'
  },
  SUBSCRIPTION_EXPIRED: {
    bg: 'Абонаментът ви е изтекъл',
    en: 'Your subscription has expired'
  },
  SUBSCRIPTION_INACTIVE: {
    bg: 'Абонаментът ви не е активен',
    en: 'Your subscription is not active'
  },

  // Data errors
  FETCH_FAILED: {
    bg: 'Неуспешно зареждане на данни за арбитражи',
    en: 'Failed to fetch surebet data'
  },
  INVALID_DATA: {
    bg: 'Невалидни данни за арбитражи',
    en: 'Invalid surebet data'
  },
  NO_DATA: {
    bg: 'Няма налични арбитражни възможности в момента',
    en: 'No arbitrage opportunities available at the moment'
  },
  DATA_VALIDATION_ERROR: {
    bg: 'Грешка при валидация на данни',
    en: 'Data validation error'
  },

  // Validation errors
  REQUIRED_FIELD: {
    bg: 'Това поле е задължително',
    en: 'This field is required'
  },
  INVALID_EMAIL: {
    bg: 'Невалиден имейл адрес',
    en: 'Invalid email address'
  },
  INVALID_PASSWORD: {
    bg: 'Невалидна парола',
    en: 'Invalid password'
  },
  PASSWORDS_DO_NOT_MATCH: {
    bg: 'Паролите не съвпадат',
    en: 'Passwords do not match'
  },

  // Rate limiting
  RATE_LIMIT: {
    bg: 'Твърде много опити за обновяване. Моля, изчакайте малко',
    en: 'Too many refresh attempts. Please wait a moment'
  },

  // Generic errors
  UNKNOWN_ERROR: {
    bg: 'Възникна непредвидена грешка',
    en: 'An unexpected error occurred'
  },
  SERVER_ERROR: {
    bg: 'Грешка в сървъра. Моля, опитайте по-късно',
    en: 'Server error. Please try again later'
  }
};

/**
 * Success Message Constants
 */
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: {
    bg: 'Успешно влизане',
    en: 'Login successful'
  },
  REGISTRATION_SUCCESS: {
    bg: 'Регистрацията е успешна',
    en: 'Registration successful'
  },
  PROFILE_UPDATED: {
    bg: 'Профилът е актуализиран',
    en: 'Profile updated'
  },
  SUBSCRIPTION_ACTIVATED: {
    bg: 'Абонаментът е активиран успешно',
    en: 'Subscription activated successfully'
  },
  DATA_LOADED: {
    bg: 'Данните са заредени успешно',
    en: 'Data loaded successfully'
  },
  DATA_REFRESHED: {
    bg: 'Данните са обновени успешно',
    en: 'Data refreshed successfully'
  },
  PASSWORD_RESET_SENT: {
    bg: 'Линк за нулиране на парола е изпратен',
    en: 'Password reset link sent'
  },
  PASSWORD_RESET_SUCCESS: {
    bg: 'Паролата е нулирана успешно',
    en: 'Password reset successful'
  }
};

/**
 * Refresh Interval Configuration
 */
export const REFRESH_INTERVALS = {
  AUTO_REFRESH_SUREBETS: 600000,      // 10 minutes - Auto-refresh surebet data
  MIN_REFRESH_INTERVAL: 60000,        // 1 minute - Minimum time between manual refreshes
  MAX_RETRY_ATTEMPTS: 3,               // Maximum retry attempts
  RETRY_DELAY: 2000,                   // 2 seconds - Delay between retry attempts
  TOKEN_REFRESH_INTERVAL: 1740000,     // ~29 minutes - Refresh token before expiry (30 min token)
  SUBSCRIPTION_CHECK_INTERVAL: 300000, // 5 minutes - Check subscription status
  SESSION_TIMEOUT: 1800000             // 30 minutes - Session timeout
};

/**
 * Validation Rules and Limits
 */
export const VALIDATION_RULES = {
  // Email validation
  EMAIL_REGEX: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,

  // Password validation
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 128,
  PASSWORD_REGEX: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,

  // Stake validation
  STAKE_MIN: 0.01,
  STAKE_MAX: 100000,
  STAKE_PRECISION: 2,

  // Odds validation
  ODDS_MIN: 1.01,
  ODDS_MAX: 1000,
  ODDS_PRECISION: 2,

  // Margin validation
  MARGIN_MIN: -100,   // -100%
  MARGIN_MAX: 100,     // 100%
  MARGIN_PRECISION: 4,

  // Percentage validation
  PERCENTAGE_MIN: 0,
  PERCENTAGE_MAX: 100,
  PERCENTAGE_PRECISION: 2,

  // Coefficient validation
  COEFFICIENT_MIN: 1.01,
  COEFFICIENT_MAX: 1000,
  COEFFICIENT_PRECISION: 2,

  // Phone validation (Bulgarian)
  PHONE_REGEX: /^(?:\+359|0)?[1-9]\d{7,8}$/,

  // URL validation
  URL_REGEX: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/,

  // Required fields
  REQUIRED_EVENT_FIELDS: ['id', 'date', 'time', 'teamOne', 'teamTwo', 'championship', 'sport'],
  REQUIRED_MARGIN_FIELDS: ['value', 'coefficients'],
  REQUIRED_COEFFICIENT_FIELDS: ['bookmaker', 'type', 'value']
};

/**
 * Local Storage Keys
 */
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'arbiwin_auth_token',
  REFRESH_TOKEN: 'arbiwin_refresh_token',
  USER_DATA: 'arbiwin_user_data',
  LANGUAGE: 'arbiwin_language',
  THEME: 'arbiwin_theme',
  BOOKMAKER_FILTERS: 'arbiwin_bookmaker_filters',
  USER_PREFERENCES: 'arbiwin_user_preferences',
  LAST_REFRESH: 'arbiwin_last_refresh',
  CALCULATOR_HISTORY: 'arbiwin_calculator_history',
  SUBSCRIPTION_STATUS: 'arbiwin_subscription_status'
};

/**
 * Application Limits
 */
export const LIMITS = {
  MAX_CONCURRENT_REQUESTS: 10,
  MAX_RETRY_ATTEMPTS: 3,
  MAX_LOGIN_ATTEMPTS: 5,
  MAX_ERROR_LOGS: 100,
  MAX_CALCULATOR_HISTORY: 50,
  MAX_SUREBET_DISPLAY: 500,
  MAX_BOOKMAKER_FILTERS: 20,
  MAX_SEARCH_RESULTS: 100,
  MAX_UPLOAD_SIZE: 5242880,  // 5MB in bytes
  MAX_PASSWORD_LENGTH: 128,
  MAX_USERNAME_LENGTH: 50,
  MAX_EMAIL_LENGTH: 255
};

/**
 * Pagination Constants
 */
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MIN_PAGE_SIZE: 5,
  MAX_PAGE_SIZE: 100,
  DEFAULT_PAGE: 1
};

/**
 * Date and Time Constants
 */
export const DATE_TIME = {
  TIMEZONE: 'Europe/Sofia',
  DATE_FORMAT_BG: 'DD.MM.YYYY',
  DATE_FORMAT_EN: 'MM/DD/YYYY',
  TIME_FORMAT: 'HH:mm',
  DATETIME_FORMAT_BG: 'DD.MM.YYYY HH:mm',
  DATETIME_FORMAT_EN: 'MM/DD/YYYY HH:mm',
  ISO_DATE_FORMAT: 'YYYY-MM-DD',
  ISO_DATETIME_FORMAT: 'YYYY-MM-DDTHH:mm:ss'
};

/**
 * UI Constants
 */
export const UI_CONSTANTS = {
  ANIMATION_DURATION: {
    FAST: 150,
    NORMAL: 300,
    SLOW: 500,
    VERY_SLOW: 1000
  },
  BREAKPOINTS: {
    SM: 640,
    MD: 768,
    LG: 1024,
    XL: 1280,
    XXL: 1536
  },
  MODAL_TYPES: {
    CONFIRMATION: 'confirmation',
    ERROR: 'error',
    INFO: 'info',
    CALCULATOR: 'calculator',
    SUBSCRIPTION: 'subscription',
    FILTERS: 'filters'
  },
  TOAST_TYPES: {
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info'
  },
  LOADING_STATES: {
    IDLE: 'idle',
    LOADING: 'loading',
    REFRESHING: 'refreshing',
    SUBMITTING: 'submitting',
    RETRYING: 'retrying'
  }
};

/**
 * Feature Flags
 */
export const FEATURE_FLAGS = {
  // Phase 1 features (always enabled)
  PHASE_1_COMPLETE: true,

  // Phase 2 features (disabled until Phase 2)
  AI_INTEGRATION: false,
  AI_ANALYSIS: false,
  AI_RECOMMENDATIONS: false,
  ENHANCED_PREDICTIONS: false,
  PERSONALIZED_INSIGHTS: false,

  // Development features
  DEBUG_PANEL: process.env.NODE_ENV === 'development',
  MOCK_DATA: process.env.REACT_APP_USE_MOCK_DATA === 'true',
  PERFORMANCE_MONITORING: process.env.REACT_APP_PERFORMANCE_MONITORING !== 'false'
};

/**
 * Event Names for Custom Events
 */
export const EVENTS = {
  AUTH_LOGIN_SUCCESS: 'auth:loginSuccess',
  AUTH_LOGOUT: 'auth:logout',
  AUTH_TOKEN_EXPIRED: 'auth:tokenExpired',
  AUTH_REQUIRED: 'auth:required',

  SUBSCRIPTION_ACTIVATED: 'subscription:activated',
  SUBSCRIPTION_EXPIRED: 'subscription:expired',
  SUBSCRIPTION_REQUIRED: 'subscription:required',

  SUREBET_DATA_UPDATED: 'surebet:dataUpdated',
  SUREBET_REFRESH_STARTED: 'surebet:refreshStarted',
  SUREBET_REFRESH_COMPLETED: 'surebet:refreshCompleted',

  CALCULATOR_OPENED: 'calculator:opened',
  CALCULATOR_CALCULATED: 'calculator:calculated',

  LANGUAGE_CHANGED: 'language:changed',
  THEME_CHANGED: 'theme:changed',

  ERROR_OCCURRED: 'error:occurred',
  ERROR_RESOLVED: 'error:resolved'
};

// Export all constants as default object
export default {
  API_ENDPOINTS,
  API_TIMEOUTS,
  HTTP_STATUS,
  USER_ROLES,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  SUBSCRIPTION_STATUS,
  MARKET_TYPES,
  COEFFICIENT_TYPES,
  BOOKMAKERS,
  SPORTS,
  LANGUAGES,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  REFRESH_INTERVALS,
  VALIDATION_RULES,
  STORAGE_KEYS,
  LIMITS,
  PAGINATION,
  DATE_TIME,
  UI_CONSTANTS,
  FEATURE_FLAGS,
  EVENTS
};

